import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

export default function Branches() {
  const { isOrgAdmin } = useAuth();
  const { companies } = useCompany() || {};
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/branches");
      setBranches(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";

  const toggleActive = async (branch) => {
    try {
      await api.patch(`/masters/branches/${branch.id}`, { is_active: !branch.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <PageHeader
        title="Branches"
        description="Branch offices or sites under each company, filterable by company."
        action={isOrgAdmin && <CreateBranchDialog companies={companies} onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && branches.length === 0 ? (
          <EmptyState title="No branches yet" description="Add a company first, then create its branches here." testId="branches-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>State</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b) => (
                <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{b.code}</TableCell>
                  <TableCell className="text-foreground">{b.name}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(b.company_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{b.state || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={b.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(b)} data-testid={`branch-active-toggle-${b.code}`} />
                      <Badge variant={b.is_active ? "default" : "secondary"}>{b.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateBranchDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", state: "", address: "", company_id: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/branches", form);
      toast.success(`Branch ${form.code} created`);
      setForm({ code: "", name: "", state: "", address: "", company_id: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-branch-button"><Plus className="h-4 w-4" /> New Branch</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Branch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-branch-form">
          <div>
            <Label>Company</Label>
            <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger data-testid="branch-company-select" className="mt-1.5"><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                {(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input required data-testid="branch-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder="HQ" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="branch-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="Head Office" />
              <FieldError error={errors.name} />
            </div>
          </div>
          <div>
            <Label>State</Label>
            <Input data-testid="branch-state-input" value={form.state} onChange={set("state")} className="mt-1.5" placeholder="Rajasthan" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !form.company_id} data-testid="create-branch-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
